import { toast } from 'sonner';
import type { FieldErrors, FieldValues } from 'react-hook-form';

/**
 * Return the first non-empty error message from an RHF `errors` object, or
 * `undefined` when every entry is clean. Used by every form's `onInvalid`
 * handler to surface a single toast for the highest-priority validation
 * failure (per CLAUDE.md rule #5 — error text goes through `toast.*`, only
 * field visuals like `border-destructive` may stay inline).
 *
 * Iteration order matches the schema's field declaration order in zod 4,
 * so the first message corresponds to the topmost invalid field in the
 * form. Refinements (e.g. `password === confirmPassword`) appear under the
 * path they target, so a confirmPassword mismatch surfaces last.
 */
export function firstErrorMessage<T extends FieldValues>(
  errors: FieldErrors<T>,
): string | undefined {
  for (const entry of Object.values(errors)) {
    const message = (entry as { message?: string } | undefined)?.message;
    if (message) return message;
  }
  return undefined;
}

/* ── zod safeParse twin ────────────────────────────────────────────────────── */

/**
 * The success half of a zod `safeParse` result. Declared structurally rather
 * than imported from zod so this module does not depend on which sub-path zod
 * exports its result types from.
 */
interface SafeParseSuccess<T> {
  success: true;
  data: T;
}

/** The failure half — only the part this helper reads. */
interface SafeParseFailure {
  success: false;
  error: { issues: ReadonlyArray<{ message: string }> };
}

/**
 * Dialogs that validate with a shared zod schema (rather than react-hook-form)
 * all repeated the same four lines: check `success`, take the first issue's
 * message, toast it, bail. This is that block, so the convention — **one**
 * toast for the highest-priority failure, per CLAUDE.md rule #5 — lives beside
 * its react-hook-form twin {@link firstErrorMessage} instead of being retyped
 * per dialog.
 *
 * Written as a type predicate so `result.data` stays typed after the guard:
 *
 * ```ts
 * const parsed = someSchema.safeParse(form);
 * if (!validOrToast(parsed, t)) return;
 * await save(parsed.data);   // narrowed to the success shape
 * ```
 *
 * Issue order matches the schema's field declaration order in zod 4, so the
 * message shown is the topmost invalid field.
 *
 * @param result - The value returned by `schema.safeParse(...)`.
 * @param t      - The caller's `t` function; issue messages are i18n keys.
 * @returns True when valid. When invalid, toasts and returns false.
 */
export function validOrToast<T>(
  result: SafeParseSuccess<T> | SafeParseFailure,
  t: (key: string) => string,
): result is SafeParseSuccess<T> {
  if (result.success) return true;
  const message = result.error.issues[0]?.message;
  if (message) toast.error(t(message));
  return false;
}
